/**
 * One phase's week pattern, projected out of the draft into the shape the v3 canon draws.
 *
 * `docs/design/2026-08-01-plan-builder-pattern-fullpage-v3.html` is the canon and this module is
 * its data half: seven fixed weekday columns, a session card per programmed day, two named blocks
 * inside each card, and numbered positions where a superset is ONE position with lettered members.
 *
 * Everything a component would otherwise have to decide is decided here — which weekday is empty,
 * which exercises are warm-up, where a superset starts and ends, how many sets count. A component
 * that has to work any of that out is a component nobody can test.
 *
 * ── Seven columns, always ────────────────────────────────────────────────────────────────────
 * The canon's note is explicit that the day HEADER belongs to the week and not to the training:
 * "Poniedziałek–niedziela stoją zawsze w tym samym miejscu … karta sesji siedzi w gnieździe pod
 * nagłówkiem". So an unprogrammed weekday is a rendered column with an empty slot reading
 * "Przerwa", never an absent column — the week's shape is only legible when the gaps are visible,
 * and the empty slot is the drop target PR-E will attach to.
 *
 * ── Which canvas a plan gets is decided ABOVE this module ────────────────────────────────────
 * A day on the rhythm TAPE carries no weekday — it carries rest-day spacing — so this projection
 * used to publish a `kind` field for a screen to refuse such a plan with. The 04.08 tape is what
 * that refusal was waiting for: `usePlanBuilder` reads `PlanDraft.mode` and mounts one of two canvases,
 * `tapeProjection` answers the other one, and nothing ever asked this projection what kind of plan
 * it was looking at. The field and its type are gone rather than kept "in case" — a discriminant no
 * reader consults is a claim that something branches on it, and the one place that reads the mode
 * is the one place that should.
 *
 * The projection itself is mode-blind on purpose, exactly as `tapeProjection` is: both draw days out
 * of a phase, and refusing in two places would put the mode check in two places.
 *
 * ── Duration comes only from what the trainer authored ───────────────────────────────────────
 * Execution ADR §1: "session duration is the authored `EstimatedDurationMinutes` only". Nothing
 * here estimates one from set counts or rest times. A session with none omits the figure, and the
 * week's average is taken over the days that HAVE one — an average that quietly counted a missing
 * duration as zero would report a lighter week than the trainer programmed.
 */

import { phaseStartCycles } from './cycleWindow';
import { countingSetCount, isWarmupRole } from './setCounting';
import type { DayDraft, ExerciseDraft, PhaseDraft, PlanDraft } from './planDraft.types';

/** Monday is 1, Sunday is 7 — the backend's `DayOfWeek`, and the canon's column order. */
export const WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

/** One exercise, as a row of the main-work list. */
export interface ExerciseRow {
  readonly slotToken: string;
  readonly exerciseId: number;
  readonly name: string;
  /**
   * The position marker: `"1"` for a plain exercise, `"2a"` / `"2b"` for the members of a bracketed
   * position. The canon draws the letter on the member and the bare number on the frame.
   *
   * **`null` when the row occupies no main-work position**, which happens in exactly one shape: a
   * warm-up-ROLE exercise that a degenerate group left standing among the singles. It is real work
   * the trainer authored and it is drawn, but numbering it would count preparation as one of the
   * day's positions — and the number beside it would then disagree with "5 ćw." two lines above.
   * Inside a mini-group the same warm-up DOES carry a letter, because there the bracket, not the
   * number, is what says it is a ramp.
   */
  readonly ordinal: string | null;
  /** How many of this exercise's sets count as work. Feeds the muscle attribution. */
  readonly countingSetCount: number;
  /**
   * Whether this row is a warm-up-ROLE exercise, and therefore outside the set counter and outside
   * the muscle attribution entirely (`setCounting.ts`, `isWarmupRole`).
   *
   * It is on the row rather than derived by the caller because a warm-up can reach the main-work
   * list — a GROUPED one is bracketed with the exercise it prepares and must render there rather
   * than be hoisted. Two callers need the answer for two different reasons: the projection excludes
   * the row from `countedExercises`, and the card renders it in the warm-up's blue rather than as
   * another counted exercise.
   */
  readonly isWarmup: boolean;
}

/**
 * One position in the main-work list.
 *
 * A **superset** occupies ONE position with two or more members — the canon's note: "jeden numer
 * pozycji, szyna spinająca dwa wiersze i podpis »Superset«" — so a pair reads as one programming
 * decision rather than two exercises that happen to be adjacent.
 *
 * A **miniGroup** is the other bracketed shape: a warm-up deliberately paired with the ONE exercise
 * it prepares. It is a position of the main work, not a member of the day's warm-up block, because
 * hoisting it would destroy the very pairing the trainer authored — the warm-up would read as
 * general preparation for the whole session instead of the specific ramp for that lift.
 */
export type ExerciseBlock =
  | { readonly kind: 'single'; readonly ordinal: string | null; readonly exercise: ExerciseRow }
  | { readonly kind: 'miniGroup'; readonly ordinal: string; readonly members: readonly ExerciseRow[] }
  | { readonly kind: 'superset'; readonly ordinal: string; readonly members: readonly ExerciseRow[] };

/**
 * The day's own warm-up block: names only, no parameters — a different class of work, not a badge.
 *
 * Holds the STANDALONE warm-ups only. One bracketed with a specific exercise renders as a
 * `miniGroup` inside the main work instead; see `ExerciseBlock`.
 */
export interface WarmupBlock {
  readonly exercises: readonly ExerciseRow[];
}

/** One programmed day's card. */
export interface DayCard {
  readonly slotToken: string;
  /** The session's name, falling back to the day's. `null` when neither was authored. */
  readonly title: string | null;
  /** The authored estimate in minutes, or `null`. Never derived. */
  readonly durationMinutes: number | null;
  /** Sets that count, across the main work only. Warm-up is outside the counter. */
  readonly countingSetCount: number;
  /** `null` when the day programmes no standalone warm-up — an absent block, not an empty one. */
  readonly warmup: WarmupBlock | null;
  readonly blocks: readonly ExerciseBlock[];
  /** How many exercises the main work holds. The canon's "5 ćw." Excludes bracketed warm-ups. */
  readonly mainExerciseCount: number;
  /**
   * Every COUNTED main-work exercise, flattened — what the day's muscle load is computed over. A
   * warm-up bracketed into the main work is drawn there but never counted here.
   */
  readonly countedExercises: readonly ExerciseRow[];
}

/** One of the seven columns. `day` is `null` on a weekday the phase does not programme. */
export interface WeekdayColumn {
  /** 1 (Monday) – 7 (Sunday). */
  readonly weekday: number;
  readonly day: DayCard | null;
}

/** The phase the week belongs to, as the phase bar and the context bar read it. */
export interface PhaseSummary {
  /** Zero-based index into `PlanDraft.phases` — what `?phase=` resolves to. */
  readonly index: number;
  readonly name: string;
  readonly type: 'build' | 'deload';
  /** Absolute first cycle, 1-based. */
  readonly startCycle: number;
  readonly cycleCount: number;
  readonly serverId: number | null;
}

export interface WeekProjection {
  readonly phase: PhaseSummary;
  /** Always seven, always Monday first. */
  readonly columns: readonly WeekdayColumn[];
  /**
   * The mean authored duration over the days that carry one, rounded to a whole minute; `null`
   * when no day in the phase authored a duration. The canon's "~58 min / dzień śr."
   */
  readonly averageDurationMinutes: number | null;
  /**
   * Days the seven columns cannot hold. Still counted in the load and in the average — see
   * `countedDays` — because they are training the trainer programmed.
   */
  readonly overflowDays: readonly DayCard[];
  /**
   * WHY each of them overflowed, counted. The two causes are different facts about the plan and the
   * banner has to be able to say which: a day with no weekday was never placed, while a day whose
   * weekday is already taken WAS placed and lost the column to a sibling.
   *
   * They were folded into one count, and the banner therefore stated the wrong cause on the more
   * common of the two: a phase with two days on one weekday overflows because the column is TAKEN,
   * and the banner said the day had no weekday at all. The mode flip that used to produce a whole
   * phase of them at once is gone (R1, 16 August 2026); the phone still authors the shape, and so
   * does adding a second row to a weekday from the calendar's day editor.
   *
   * A pair of numbers rather than a reason on each `DayCard`: nothing renders a per-day reason, and
   * a field the card carries for a sentence three elements away is a field that goes stale.
   */
  readonly overflowCauses: {
    /** No `dayOfWeek` at all, or one outside 1–7. */
    readonly noWeekday: number;
    /** A weekday another day of the same phase already stands on. */
    readonly duplicateWeekday: number;
  };
}

/**
 * The phase a `?phase=` parameter resolves to, clamped into the plan.
 *
 * Returns `null` only for a plan with no phases at all — which is a real state (`pages/plan-library`
 * already renders "Pusty program" for one) and not an error.
 */
export function resolvePhaseIndex(draft: PlanDraft, requested: number): number | null {
  if (draft.phases.length === 0) return null;
  return Math.min(Math.max(0, requested), draft.phases.length - 1);
}

function toExerciseRow(exercise: ExerciseDraft, ordinal: string | null): ExerciseRow {
  return {
    slotToken: exercise.slotToken,
    exerciseId: exercise.exerciseId,
    name: exercise.name,
    ordinal,
    countingSetCount: countingSetCount(exercise.sets),
    isWarmup: isWarmupRole(exercise.role),
  };
}

/**
 * Is this a STANDALONE day warm-up — the kind that belongs in the day's own warm-up block?
 *
 * Ported verbatim from mobile's `isStandaloneDayWarmup`, and the `group === null` half is the whole
 * point: a warm-up carrying a group was deliberately bracketed with the exercise it prepares, and
 * belongs beside it in the main work.
 */
function isStandaloneDayWarmup(exercise: ExerciseDraft): boolean {
  return isWarmupRole(exercise.role) && exercise.group == null;
}

/**
 * Group the day's work into numbered positions, bracketing supersets and warm-up mini-groups.
 *
 * Ported from mobile's `toDayBlocks`, which is the single source of this rule, and the port is
 * faithful because the two clients render the same plan.
 *
 * ── The walk runs over the FULL ordered list ─────────────────────────────────────────────────
 * Everything except the standalone day warm-ups, in the order the trainer authored. That ordering
 * is load-bearing and getting it wrong is the defect this replaced: filtering warm-ups out FIRST
 * and bracketing what remained made two exercises that share a group but are separated by a warm-up
 * become adjacent, and they then fused into a superset the trainer never programmed — a rail drawn
 * across a pairing that does not exist. Contiguity has to be measured over the real list.
 *
 * ── One run, three outcomes ──────────────────────────────────────────────────────────────────
 * A run is `exercises[index]` plus every IMMEDIATELY following exercise carrying the same non-null
 * group. Two exercises sharing a group with anything between them are two runs, never one.
 *
 *   · a warm-up plus exactly ONE work exercise → `miniGroup`: the ramp and the lift it prepares.
 *   · no warm-up and TWO OR MORE work exercises → `superset`.
 *   · anything else — a run of one, a warm-up alone, a warm-up with two work exercises — falls
 *     back to plain singles. A bracket nobody can name is not drawn as a bracket, and nothing is
 *     ever dropped. A warm-up that lands there is drawn WITHOUT a position number and does not
 *     advance the counter: it is preparation, and every other count on the card already says so.
 *
 * A "superset" of one is therefore a single: a group letter left behind after its partner was
 * deleted is not a pairing, and must not render a rail and a "Superset" label around one row.
 */
function toBlocks(exercises: readonly ExerciseDraft[]): ExerciseBlock[] {
  const blocks: ExerciseBlock[] = [];
  let position = 0;
  let index = 0;

  while (index < exercises.length) {
    const first = exercises[index];
    // `noUncheckedIndexedAccess`: the loop bound already guarantees this, and the compiler cannot
    // see it. Breaking rather than asserting keeps the impossible case harmless instead of loud.
    if (first === undefined) break;
    const group = first.group;

    let end = index + 1;
    if (group != null) {
      while (end < exercises.length && exercises[end]?.group === group) end += 1;
    }

    const run = exercises.slice(index, end);
    const warmupCount = run.filter((exercise) => isWarmupRole(exercise.role)).length;
    const workCount = run.length - warmupCount;

    // 'a', 'b', 'c' … — the canon's `2a` / `2b`. Past 26 members the wire's 20-exercise cap has
    // long since been hit, so the sequence cannot run out in practice. Members keep their AUTHORED
    // order: the letters say what is performed first, so re-sorting them would misreport the work.
    const lettered = (ordinal: string): ExerciseRow[] =>
      run.map((exercise, offset) =>
        toExerciseRow(exercise, `${ordinal}${String.fromCharCode(97 + offset)}`),
      );

    if (warmupCount > 0 && workCount === 1) {
      position += 1;
      const ordinal = String(position);
      blocks.push({ kind: 'miniGroup', ordinal, members: lettered(ordinal) });
    } else if (warmupCount === 0 && workCount > 1) {
      position += 1;
      const ordinal = String(position);
      blocks.push({ kind: 'superset', ordinal, members: lettered(ordinal) });
    } else {
      // The degenerate shapes. Each member becomes its own row, so a group that cannot be drawn as
      // a bracket still shows every exercise it holds.
      //
      // A WARM-UP left standing here takes no position number, and the counter does not advance for
      // it. The alternative was measured against the two counts the card already prints: the main
      // work's "N ćw." and the day's set total both exclude warm-ups (`countedExercises`), so
      // numbering one would put a "3" beside a row on a card that says the day trains two
      // exercises. The row is still drawn, still in the trainer's authored order, and still in the
      // warm-up's own ink — exactly the treatment a mini-group member gets. What it does not get is
      // a claim to be main work.
      for (const exercise of run) {
        if (isWarmupRole(exercise.role)) {
          blocks.push({ kind: 'single', ordinal: null, exercise: toExerciseRow(exercise, null) });
          continue;
        }
        position += 1;
        const ordinal = String(position);
        blocks.push({ kind: 'single', ordinal, exercise: toExerciseRow(exercise, ordinal) });
      }
    }
    index = end;
  }

  return blocks;
}

/** Every row a block holds, whatever its kind. */
function rowsOf(block: ExerciseBlock): readonly ExerciseRow[] {
  return block.kind === 'single' ? [block.exercise] : block.members;
}

/**
 * One day → the card the week draws, and — since PR-B2C — the STRUCTURE the day dock lists.
 *
 * Exported on its second consumer rather than kept private. The dock renders the same day as one
 * vertical list with the same numbered positions and the same superset bracketing; re-deriving that
 * in a feature would be a second copy of `toBlocks`, and the two would disagree the first time a
 * degenerate group appeared in one and not the other. The dock pairs each row's `slotToken` back to
 * the day's own exercises for the parameters this projection deliberately does not carry.
 */
export function dayCard(day: DayDraft): DayCard {
  const warmupExercises = day.exercises.filter(isStandaloneDayWarmup);
  const work = day.exercises.filter((exercise) => !isStandaloneDayWarmup(exercise));

  const blocks = toBlocks(work);
  // A warm-up that reached the main work — a mini-group member, or a degenerate group's leftover —
  // is still a warm-up: outside the set counter and outside the muscle attribution, exactly as it
  // would be in the day's own warm-up block. The ROLE decides that, never the position.
  const countedExercises = blocks.flatMap(rowsOf).filter((row) => !row.isWarmup);

  return {
    slotToken: day.slotToken,
    // The session's name titles the card — the canon's "Push A" — with the day's name as the
    // fallback, because a legacy day may carry a name where its session does not.
    title: day.sessionName?.trim() || day.name?.trim() || null,
    durationMinutes: day.durationMinutes,
    countingSetCount: countedExercises.reduce((total, row) => total + row.countingSetCount, 0),
    // Warm-up exercises are listed by NAME and nothing else: the canon draws no parameters on them,
    // which is what makes the block read as a different class of work rather than a short exercise
    // list. They are numbered from 1 within their own block.
    warmup:
      warmupExercises.length === 0
        ? null
        : {
            exercises: warmupExercises.map((exercise, offset) =>
              toExerciseRow(exercise, String(offset + 1)),
            ),
          },
    blocks,
    // The main work's exercise count is exactly what counts — a bracketed warm-up is drawn in the
    // list but is not one of the day's exercises any more than a hoisted one would be.
    mainExerciseCount: countedExercises.length,
    countedExercises,
  };
}

/**
 * The position marker each exercise of a day's list carries in the builder: `1`, `2a`, `2b`, `3`,
 * from the same walk `dayCard` makes. `null` for a warm-up, which holds no position (the day form
 * marks its own warm-up run `R`). Exported for Buddy's proposal (board U), which names where an
 * exercise it writes will stand, on a day it has not written yet.
 */
export function exerciseOrdinals(exercises: readonly ExerciseDraft[]): ReadonlyMap<string, string | null> {
  const rows = toBlocks(exercises.filter((exercise) => !isStandaloneDayWarmup(exercise))).flatMap(rowsOf);
  return new Map([
    ...exercises.filter(isStandaloneDayWarmup).map((exercise): [string, string | null] => [exercise.slotToken, null]),
    ...rows.map((row): [string, string | null] => [row.slotToken, row.ordinal]),
  ]);
}

function averageDuration(days: readonly DayCard[]): number | null {
  const authored = days
    .map((day) => day.durationMinutes)
    .filter((minutes): minutes is number => minutes != null);
  if (authored.length === 0) return null;
  return Math.round(authored.reduce((sum, minutes) => sum + minutes, 0) / authored.length);
}

/**
 * One phase, as every bar and every canvas reads it.
 *
 * Exported on its second consumer — `tapeProjection` answers the same question about the same phase
 * and a second copy would drift the moment a field is added here.
 */
export function toPhaseSummary(
  phase: PhaseDraft,
  index: number,
  startCycle: number,
): PhaseSummary {
  return {
    index,
    name: phase.name,
    type: phase.type,
    // HANDED IN rather than read off the node. `PhaseDraft.startCycle` is the server's, restamped
    // by the phase operations — and `responseToDraft` falls back to `1` for a phase the wire sent no
    // start for, which on the second such phase would put two phases on cycle 1 and print two tabs
    // claiming the same weeks. `phaseStartCycles` derives it from the lengths, which is the same
    // walk every gate already trusts. The field stays: `phaseEdits` measures its own delta against
    // it, and that is exactly the reading that must not be recomputed.
    startCycle,
    cycleCount: phase.cycles,
    serverId: phase.serverId,
  };
}

/**
 * Project one phase of the draft into the week the canon draws.
 *
 * @param draft The loaded document.
 * @param phaseIndex Zero-based, already resolved by `resolvePhaseIndex`. Out-of-range is clamped
 *   here too rather than throwing: a stale `?phase=` in a bookmarked URL is a normal thing to
 *   receive, and the honest answer is the nearest phase that exists.
 * @returns `null` only when the plan has no phases.
 */
export function weekProjection(draft: PlanDraft, phaseIndex: number): WeekProjection | null {
  const index = resolvePhaseIndex(draft, phaseIndex);
  if (index === null) return null;
  const phase = draft.phases[index];
  // `resolvePhaseIndex` already clamped into the array; this satisfies `noUncheckedIndexedAccess`
  // without an assertion, and returns the same "no phases" answer if it ever were wrong.
  if (phase === undefined) return null;

  const cards = new Map<number, DayCard>();
  const overflowDays: DayCard[] = [];
  let noWeekday = 0;
  let duplicateWeekday = 0;

  for (const day of phase.days) {
    const card = dayCard(day);
    const weekday = day.dayOfWeek;
    // A day with no weekday, or a SECOND day claiming a weekday already taken, cannot occupy a
    // column. Both are shapes the wire permits and the mode rules discourage rather than forbid,
    // and dropping such a day silently would hide programmed work from the trainer entirely — so
    // it is carried out separately and the screen says it is there.
    //
    // The two causes are counted APART, because the banner has to name the right one — see
    // `overflowCauses`. Order matters: a day with no weekday cannot also be a duplicate, so the
    // absent case is tested first and `cards.has` only ever answers for a day that named one.
    if (weekday == null || weekday < 1 || weekday > 7) {
      noWeekday += 1;
      overflowDays.push(card);
      continue;
    }
    if (cards.has(weekday)) {
      duplicateWeekday += 1;
      overflowDays.push(card);
      continue;
    }
    cards.set(weekday, card);
  }

  const columns = WEEKDAYS.map((weekday) => ({ weekday, day: cards.get(weekday) ?? null }));
  const programmed = [...cards.values(), ...overflowDays];

  return {
    phase: toPhaseSummary(phase, index, phaseStartCycles(draft.phases)[index] ?? 1),
    columns,
    averageDurationMinutes: averageDuration(programmed),
    overflowDays,
    overflowCauses: { noWeekday, duplicateWeekday },
  };
}

/**
 * The days the week's muscle load is computed over, in the ONE order everything else walks them.
 *
 * ── Why this is a function of the projection and not the page's business ─────────────────────
 * The load is an array positional against the days, and `weekViewModel` walks the columns back with
 * a cursor to put each day's bars under that day. That pairing held only as long as the page
 * flattened the columns exactly the way this walks them — a rule that lived in a comment and would
 * have survived precisely until someone sorted, filtered or reversed one of the two. It is the
 * projection's answer, so it is answered here, and `aDaysBarsRenderUnderThatDayAndNeverItsNeighbour`
 * pins it.
 *
 * ── Overflow days are IN, in both scopes ─────────────────────────────────────────────────────
 * A day with no weekday, or a second day claiming one already taken, is still training the trainer
 * programmed. It counts toward the week's load exactly as it already counts toward the duration
 * average — one definition of "the days of this phase", used by both. The alternative was a week
 * strip whose six bars silently omitted a day the banner directly above them says exists, which is
 * a harder thing to explain than a bar that includes work the grid cannot place.
 *
 * They come LAST, after the programmed columns, so a column's index into the result is unchanged by
 * whether any overflow exists at all.
 */
export function countedDays(projection: WeekProjection): readonly (readonly ExerciseRow[])[] {
  const columnDays = projection.columns
    .map((column) => column.day)
    .filter((day): day is DayCard => day !== null);
  return [...columnDays, ...projection.overflowDays].map((day) => day.countedExercises);
}

/**
 * Every phase, for the phase bar. Cheap, and it keeps the bar off the raw tree.
 *
 * The starts are derived ONCE from the lengths and handed in, rather than each summary reading the
 * captured field — see `toPhaseSummary`. Passed explicitly rather than through `.map(toPhaseSummary)`
 * for the same reason: the array's third callback argument is the array itself, so the shorthand
 * would silently hand a `PhaseDraft[]` to a `number` parameter the day one is added.
 */
export function phaseSummaries(draft: PlanDraft): readonly PhaseSummary[] {
  const starts = phaseStartCycles(draft.phases);
  return draft.phases.map((phase, index) => toPhaseSummary(phase, index, starts[index] ?? 1));
}
